import { useState, useMemo } from 'react';
import { format, startOfWeek, isBefore } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { parseDate } from '@/lib/localizedDateUtils';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, ComposedChart, Line,
} from 'recharts';
import { Calendar, TrendingUp, AlertTriangle, DollarSign, RefreshCw, CalendarDays } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FinanceNav, MoneyDisplay, BusinessUnitSelector, BrandSelector } from '@/components/finance';
import { useReceivablesPeriod, type PeriodReceivablesSummary } from '@/hooks/finance/useReceivablesWeekly';
import type { BusinessUnit } from '@/components/finance/BusinessUnitSelector';
import type { Brand } from '@/components/finance/BrandSelector';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

type ViewMode = 'week' | 'month';

export default function WeeklyReceivablesReport() {
  const [businessUnit, setBusinessUnit] = useState<BusinessUnit>('all');
  const [brand, setBrand] = useState<Brand>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('week');
  
  const { data: periodData, isLoading } = useReceivablesPeriod({
    businessUnit,
    brand,
    groupBy: viewMode,
  });

  // Calculate summary stats
  const stats = useMemo(() => {
    if (!periodData || periodData.length === 0) {
      return {
        totalAberto: 0,
        totalAtrasado: 0,
        totalPagamentos: 0,
        totalClientes: 0,
        mediaPeriodo: 0,
      };
    }

    const totalAberto = periodData.reduce((sum, w) => sum + w.total_aberto, 0);
    const totalAtrasado = periodData.reduce((sum, w) => sum + w.total_atrasado, 0);
    const totalPagamentos = periodData.reduce((sum, w) => sum + w.quantidade_pagamentos, 0);
    const avgClientes = periodData.reduce((sum, w) => sum + w.quantidade_clientes, 0);
    
    return {
      totalAberto,
      totalAtrasado,
      totalPagamentos,
      totalClientes: avgClientes,
      mediaPeriodo: totalAberto / periodData.length,
    };
  }, [periodData]);

  // Prepare chart data
  const chartData = useMemo(() => {
    if (!periodData) return [];
    
    const today = new Date();
    const currentWeekStart = startOfWeek(today, { weekStartsOn: 1 });
    
    return periodData.map(period => {
      const periodDate = parseDate(period.periodo + (viewMode === 'month' ? '-01' : ''))!;
      const isOverdue = isBefore(periodDate, viewMode === 'month' ? new Date(today.getFullYear(), today.getMonth(), 1) : currentWeekStart);
      
      return {
        periodo: viewMode === 'month' 
          ? format(periodDate, 'MMM/yy', { locale: ptBR })
          : format(periodDate, 'dd/MM', { locale: ptBR }),
        periodoFull: period.periodo_label,
        aVencer: isOverdue ? 0 : period.total_aberto - period.total_atrasado,
        atrasado: period.total_atrasado,
        total: period.total_aberto,
        pagamentos: period.quantidade_pagamentos,
        clientes: period.quantidade_clientes,
      };
    });
  }, [periodData, viewMode]);

  // Cumulative chart data
  const cumulativeData = useMemo(() => {
    if (!chartData || chartData.length === 0) return [];
    
    let cumulative = 0;
    return chartData.map(period => {
      cumulative += period.total;
      return {
        ...period,
        acumulado: cumulative,
      };
    });
  }, [chartData]);

  const periodLabel = viewMode === 'week' ? 'semanas' : 'meses';
  const periodLabelSingular = viewMode === 'week' ? 'Semana' : 'Mês';

  return (
    <div className="space-y-6">
      <FinanceNav />

      <h1 className="text-2xl font-bold tracking-tight">Recebíveis por Período</h1>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-2">
              <Label>Business Unit</Label>
              <BusinessUnitSelector
                value={businessUnit}
                onChange={setBusinessUnit}
                includeAll
              />
            </div>
            <div className="space-y-2">
              <Label>Marca</Label>
              <BrandSelector
                value={brand}
                onChange={setBrand}
                includeAll
              />
            </div>
            <div className="space-y-2">
              <Label>Agrupar por</Label>
              <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as ViewMode)} className="w-auto">
                <TabsList>
                  <TabsTrigger value="week" className="gap-2">
                    <Calendar className="h-4 w-4" />
                    Semana
                  </TabsTrigger>
                  <TabsTrigger value="month" className="gap-2">
                    <CalendarDays className="h-4 w-4" />
                    Mês
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total a Receber
            </CardTitle>
            <DollarSign className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-8 w-32 bg-muted animate-pulse rounded" />
            ) : (
              <div className="text-2xl font-bold">
                <MoneyDisplay value={stats.totalAberto} currency="BRL" />
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              {periodData?.length || 0} {periodLabel}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Em Atraso
            </CardTitle>
            <AlertTriangle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-8 w-32 bg-muted animate-pulse rounded" />
            ) : (
              <div className="text-2xl font-bold text-destructive">
                <MoneyDisplay value={stats.totalAtrasado} currency="BRL" />
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              {stats.totalAberto > 0 
                ? `${((stats.totalAtrasado / stats.totalAberto) * 100).toFixed(1)}% do total`
                : '0%'
              }
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Pagamentos
            </CardTitle>
            <Calendar className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-8 w-32 bg-muted animate-pulse rounded" />
            ) : (
              <div className="text-2xl font-bold">
                {stats.totalPagamentos.toLocaleString('pt-BR')}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              Total de parcelas
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Média/{periodLabelSingular}
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-8 w-32 bg-muted animate-pulse rounded" />
            ) : (
              <div className="text-2xl font-bold text-emerald-600">
                <MoneyDisplay value={stats.mediaPeriodo} currency="BRL" />
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              Expectativa de recebimento
            </p>
          </CardContent>
        </Card>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center py-16">
            <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Bar Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Recebíveis por {periodLabelSingular}</CardTitle>
              <CardDescription>
                Distribuição {viewMode === 'week' ? 'semanal' : 'mensal'} dos valores a receber
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis 
                    dataKey="periodo" 
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <YAxis 
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                    tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    formatter={(value: number, name: string) => {
                      const label = name === 'aVencer' ? 'A Vencer' : name === 'atrasado' ? 'Em Atraso' : name;
                      return [`R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, label];
                    }}
                    labelFormatter={(label) => {
                      const item = chartData.find(d => d.periodo === label);
                      return item?.periodoFull || label;
                    }}
                  />
                  <Legend 
                    formatter={(value) => {
                      if (value === 'aVencer') return 'A Vencer';
                      if (value === 'atrasado') return 'Em Atraso';
                      return value;
                    }}
                  />
                  <Bar dataKey="aVencer" stackId="a" fill="hsl(var(--primary))" name="aVencer" />
                  <Bar dataKey="atrasado" stackId="a" fill="hsl(var(--destructive))" name="atrasado" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Cumulative Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Evolução Acumulada</CardTitle>
              <CardDescription>
                Total acumulado de recebíveis ao longo {viewMode === 'week' ? 'das semanas' : 'dos meses'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={cumulativeData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis 
                    dataKey="periodo" 
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <YAxis 
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                    tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    formatter={(value: number, name: string) => {
                      const label = name === 'acumulado' ? 'Acumulado' : name === 'total' ? periodLabelSingular : name;
                      return [`R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, label];
                    }}
                  />
                  <Legend 
                    formatter={(value) => {
                      if (value === 'acumulado') return 'Acumulado';
                      if (value === 'total') return periodLabelSingular;
                      return value;
                    }}
                  />
                  <Bar dataKey="total" fill="hsl(var(--primary) / 0.3)" name="total" />
                  <Line 
                    type="monotone" 
                    dataKey="acumulado" 
                    stroke="hsl(var(--primary))" 
                    strokeWidth={2}
                    dot={{ fill: 'hsl(var(--primary))' }}
                    name="acumulado"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Detailed Table */}
          <Card>
            <CardHeader>
              <CardTitle>Detalhamento por {periodLabelSingular}</CardTitle>
              <CardDescription>
                Resumo detalhado de cada {viewMode === 'week' ? 'semana' : 'mês'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{periodLabelSingular}</TableHead>
                    <TableHead className="text-center">Pagamentos</TableHead>
                    <TableHead className="text-center">Clientes</TableHead>
                    <TableHead className="text-right">A Vencer</TableHead>
                    <TableHead className="text-right">Em Atraso</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {periodData?.map((period) => (
                    <TableRow key={period.periodo}>
                      <TableCell className="font-medium">
                        {period.periodo_label}
                      </TableCell>
                      <TableCell className="text-center">
                        {period.quantidade_pagamentos}
                      </TableCell>
                      <TableCell className="text-center">
                        {period.quantidade_clientes}
                      </TableCell>
                      <TableCell className="text-right">
                        <MoneyDisplay value={period.total_aberto - period.total_atrasado} currency="BRL" />
                      </TableCell>
                      <TableCell className="text-right text-destructive">
                        <MoneyDisplay value={period.total_atrasado} currency="BRL" />
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        <MoneyDisplay value={period.total_aberto} currency="BRL" />
                      </TableCell>
                    </TableRow>
                  ))}
                  {periodData && periodData.length > 0 && (
                    <TableRow className="bg-muted/50 font-bold">
                      <TableCell>Total</TableCell>
                      <TableCell className="text-center">
                        {stats.totalPagamentos}
                      </TableCell>
                      <TableCell className="text-center">-</TableCell>
                      <TableCell className="text-right">
                        <MoneyDisplay value={stats.totalAberto - stats.totalAtrasado} currency="BRL" />
                      </TableCell>
                      <TableCell className="text-right text-destructive">
                        <MoneyDisplay value={stats.totalAtrasado} currency="BRL" />
                      </TableCell>
                      <TableCell className="text-right">
                        <MoneyDisplay value={stats.totalAberto} currency="BRL" />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
